import React, { useState, useEffect } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { abacResourceService } from '../../services/abac';
import { abacResourceAttributeService } from '../../services/abac';
import { toast } from 'react-hot-toast';
import { Modal } from '../../components/ui';
import { useLanguage } from '../../state/language/LanguageProvider';

interface Resource {
  id: string;
  name: string;
  resource_type: string;
  description: string;
  status: string;
  custom_attributes: Array<{
    attribute_name: string;
    value: any;
  }>;
}

interface Attribute {
  id: string;
  attribute_name: string;
  data_type: string;
  description: string;
}

const ResourceAttributeAssignmentPage: React.FC = () => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(null);
  const [attributeValue, setAttributeValue] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch attributes
      try {
        const attributesResponse = await abacResourceAttributeService.getAll();
        setAttributes(attributesResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch attributes:', error);
        toast.error(t('abac.resourceAttributeAssignment.failedToFetchAttributes'));
        setAttributes([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('abac.resourceAttributeAssignment.failedToFetchData'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAttribute = (resource: Resource) => {
    setSelectedResource(resource);
    setShowAssignmentModal(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedResource || !selectedAttribute || !attributeValue.trim()) {
      toast.error(t('abac.resourceAttributeAssignment.pleaseFillAllFields'));
      return;
    }

    try {
      await abacResourceService.assignAttributeValues({
        entity_type: 'RESOURCE',
        entity_id: selectedResource.id,
        attribute_id: selectedAttribute.id,
        values: [attributeValue]
      });
      
      toast.success(t('abac.resourceAttributeAssignment.attributeValueAssignedSuccessfully'));
      setShowAssignmentModal(false);
      setSelectedResource(null);
      setSelectedAttribute(null);
      setAttributeValue('');
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error(t('abac.resourceAttributeAssignment.failedToAssignAttributeValue'));
    }
  };

  const handleEditAttribute = (resource: Resource) => {
    setSelectedResource(resource);
    setShowAssignmentModal(true);
  };

  const handleDeleteAttribute = async (resource: Resource, attributeName: string) => {
    if (!confirm(t('abac.resourceAttributeAssignment.confirmRemoveAttribute', { attributeName, resourceName: resource.name }))) {
      return;
    }

    try {
      // Find the attribute ID
      const attribute = attributes.find(attr => attr.attribute_name === attributeName);
      if (!attribute) {
        toast.error(t('abac.resourceAttributeAssignment.attributeNotFound'));
        return;
      }

      // Remove the attribute value (set to null or delete)
      await abacResourceService.assignAttributeValues({
        entity_type: 'RESOURCE',
        entity_id: resource.id,
        attribute_id: attribute.id,
        values: [] // Empty array to remove the value
      });
      
      toast.success(t('abac.resourceAttributeAssignment.attributeValueRemovedSuccessfully'));
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error(t('abac.resourceAttributeAssignment.failedToRemoveAttributeValue'));
    }
  };

  const getResourceTypeColor = (type: string) => {
    switch (type) {
      case 'KPI':
        return 'bg-blue-100 text-blue-800';
      case 'TRANSFORMATION':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Create entity definition for the CRUD table
  const resourceEntity = {
    name: 'Resource',
    displayName: 'Resources & Their Attributes',
    apiEndpoint: '/abac/resources',
    idField: 'id',
    nameField: 'name',
    copyable: false,
    deletable: false,
    searchable: true,
    sortable: true,
    pageSize: 20,
    fields: [
      {
        key: 'id',
        label: 'ID',
        type: 'text',
        hidden: true,
        order: 0
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        order: 1
      },
      {
        key: 'resource_type',
        label: 'Type',
        type: 'custom',
        order: 2,
        render: (value: string) => (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getResourceTypeColor(value)}`}>
            {value}
          </span>
        )
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        order: 3
      },
      {
        key: 'status',
        label: 'Status',
        type: 'text',
        order: 4
      },
      {
        key: 'custom_attributes',
        label: 'Assigned Attributes',
        type: 'custom',
        order: 5,
        render: (value: any, item: Resource) => {
          if (!value || value.length === 0) {
            return <span className="text-gray-400">{t('abac.resourceAttributeAssignment.noAttributesAssigned')}</span>;
          }
          return (
            <div className="space-y-1">
              {value.map((attr: any, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{attr.attribute_name}:</span>
                  <span className="text-sm text-gray-600">{String(attr.value || 'N/A')}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAttribute(item, attr.attribute_name);
                    }}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          );
        }
      }
    ]
  };

  // Custom service for the CRUD table
  const resourceCRUDService = {
    getAll: async (params: any = {}) => {
      const response = await abacResourceService.getAll(params);
      return response;
    },
    getById: async (id: string) => {
      const response = await abacResourceService.getAll({ id });
      return response.data?.find((resource: Resource) => resource.id === id);
    },
    create: async (_data: any) => {
      // This would typically create a new resource, but for now we'll just return success
      return { success: true };
    },
    update: async (_id: string, _data: any) => {
      // This would typically update resource data, but for now we'll just return success
      return { success: true };
    },
    delete: async (_id: string) => {
      // This would typically delete a resource, but for now we'll just return success
      return { success: true };
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('abac.resourceAttributeAssignment.loadingResources')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.resourceAttributeAssignment.title')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.resourceAttributeAssignment.description')}
        </p>
      </div>

      <FlexibleCRUDTable
        entity={resourceEntity}
        service={resourceCRUDService}
        customActions={[
          {
            label: t('abac.resourceAttributeAssignment.assignAttribute'),
            icon: () => <span className="text-blue-600">+</span>,
            onClick: handleAssignAttribute,
            variant: 'primary'
          },
          {
            label: t('abac.resourceAttributeAssignment.editAttributes'),
            icon: () => <span className="text-green-600">✎</span>,
            onClick: handleEditAttribute,
            variant: 'secondary'
          }
        ]}
        searchPlaceholder={t('abac.resourceAttributeAssignment.searchResourcesByName')}
        onOperationComplete={fetchData}
      />

      {/* Attribute Assignment Modal */}
      <Modal
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setSelectedResource(null);
          setSelectedAttribute(null);
          setAttributeValue('');
        }}
        title={t('abac.resourceAttributeAssignment.assignAttributeTo', { resourceName: selectedResource?.name || 'Resource' })}
        size="lg"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('abac.resourceAttributeAssignment.selectAttribute')}
            </label>
            <select
              value={selectedAttribute?.id || ''}
              onChange={(e) => {
                const attr = attributes.find(a => a.id === e.target.value);
                setSelectedAttribute(attr || null);
                setAttributeValue('');
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('abac.resourceAttributeAssignment.selectAnAttribute')}</option>
              {attributes.map((attr) => (
                <option key={attr.id} value={attr.id}>
                  {attr.attribute_name} ({attr.data_type})
                </option>
              ))}
            </select>
          </div>

          {selectedAttribute && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('abac.resourceAttributeAssignment.attributeValue')}
              </label>
              {selectedAttribute.data_type === 'DATE' ? (
                <input
                  type="date"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : selectedAttribute.data_type === 'BOOLEAN' ? (
                <select
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select...</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : selectedAttribute.data_type === 'NUMBER' ? (
                <input
                  type="number"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  placeholder={t('abac.resourceAttributeAssignment.enterAttributeValue', { attributeName: selectedAttribute.attribute_name })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="text"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  placeholder={t('abac.resourceAttributeAssignment.enterAttributeValue', { attributeName: selectedAttribute.attribute_name })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
              {selectedAttribute.description && (
                <p className="text-sm text-gray-500 mt-1">{selectedAttribute.description}</p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowAssignmentModal(false);
                setSelectedResource(null);
                setSelectedAttribute(null);
                setAttributeValue('');
              }}
                             className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
             >
               {t('common.cancel')}
             </button>
             <button
               onClick={handleSaveAssignment}
               disabled={!selectedAttribute || !attributeValue.trim()}
               className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {t('abac.resourceAttributeAssignment.assignAttribute')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ResourceAttributeAssignmentPage;
